import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { auth, db } from '../../firebase';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import numberToWords from 'number-to-words';
import { getAuth } from 'firebase/auth';

import {
  Box,
  Grid,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';

import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonIcon from '@mui/icons-material/Person';
import HotelIcon from '@mui/icons-material/Hotel';

const colors = {
  darkGreen: '#06362D',
  calPolyGreen: '#155332',
  vanilla: '#DEE2B1',
  gold: '#FFD500',
  goldenrod: '#D1A505',
  lightBg: '#F8F9FA',
  borderGrey: '#D0D7DE'
};

function Hotelinvoice() {
  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    }
  });

  const { documentId } = useParams();
  const [userData, setUserData] = useState(null);
  const [propData, setPropData] = useState({});
  const [bookingData, setBookingData] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  // Editable Customer States
  const [custName, setCustName] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [gender, setGender] = useState('Male');
  const [visitPurpose, setVisitPurpose] = useState('Darshan');
  const [gstName, setGstName] = useState('');
  const [gstNo, setGstNo] = useState('');
  const [bookingSource, setBookingSource] = useState('Walking');
  const [documentType, setDocumentType] = useState('');
  const [idNo, setIdNo] = useState('');
  const [adults, setAdults] = useState('');
  const [childs, setChilds] = useState('');

  // Financial States
  const [subtotal, setSubtotal] = useState('0.00');
  const [cgst, setCgst] = useState('0.00');
  const [sgst, setSgst] = useState('0.00');
  const [taxes, setTaxes] = useState('0.00');
  const [payHistory, setPayHistory] = useState([]);
  const [amountInWords, setAmountInWords] = useState('');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setUserData(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const loadPropertyAndBooking = async () => {
      const currentUser = getAuth().currentUser;
      if (!currentUser) return;

      const roleSnap = await getDoc(doc(db, 'authroles', currentUser.uid));
      if (!roleSnap.exists()) return;

      const pid = roleSnap.data().propertyId;
      const propSnap = await getDoc(doc(db, 'properties', pid));
      if (propSnap.exists()) {
        setPropData(propSnap.data());
      }

      if (documentId) {
        const docRef = doc(db, 'reservations', documentId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setBookingData(data);

          const totalAmount = parseFloat(data.totalamount || 0);
          const roomTotal = parseFloat(data.rooamount || data.totalamount || 0);

          const taxAmount = (totalAmount * 5) / 105;
          const taxableAmt = roomTotal - taxAmount;
          const halfTax = (taxAmount / 2).toFixed(2);

          setSubtotal(taxableAmt.toFixed(2));
          setTaxes(taxAmount.toFixed(2));
          setCgst(halfTax);
          setSgst(halfTax);

          if (data.totalamount) {
            setAmountInWords(numberToWords.toWords(Math.round(data.totalamount)));
          }
        }

        const q1 = query(
          collection(db, 'payhistory'),
          orderBy('createdAt', 'asc'),
          where('bookingid', '==', documentId)
        );

        onSnapshot(q1, async (querySnapshot1) => {
          const combinedData = [];
          for (const doc1 of querySnapshot1.docs) {
            const payData = doc1.data();
            let paymode1 = '';
            let paymode2 = '';

            if (payData.paymode1) {
              const p1Snap = await getDoc(doc(db, 'paymentmodes', payData.paymode1));
              if (p1Snap.exists()) paymode1 = p1Snap.data().paymode;
            }
            if (payData.paymode2) {
              const p2Snap = await getDoc(doc(db, 'paymentmodes', payData.paymode2));
              if (p2Snap.exists()) paymode2 = p2Snap.data().paymode;
            }

            const date = payData.createdAt?.toDate ? payData.createdAt.toDate() : new Date();
            const createdat = moment(date).format('DD-MM-YYYY HH:mm:ss');

            combinedData.push({
              id: doc1.id,
              data: payData,
              paymode1,
              paymode2,
              createdat
            });
          }
          setPayHistory(combinedData);
        });
      }
    };

    loadPropertyAndBooking();
  }, [documentId]);

  const handleOpenEdit = () => {
    if (!bookingData || !bookingData.customers) return;
    const c = bookingData.customers;
    setCustName(c.custname || '');
    setMobile(c.mobile || '');
    setEmail(c.email || '');
    setAddress(c.address || '');
    setCity(c.city || '');
    setState(c.state || '');
    setGender(c.gender || 'Male');
    setGstName(c.gstname || '');
    setGstNo(c.gstno || '');
    setDocumentType(c.documenttype || '');
    setIdNo(c.idno || '');
    setVisitPurpose(bookingData.visitpurpose || 'Darshan');
    setBookingSource(bookingData.bookingsource || 'Walking');
    setAdults(bookingData.adults || '');
    setChilds(bookingData.childs || '');
    setOpenModal(true);
  };

  const updateCust = async () => {
    try {
      const customerData = {
        custname: custName,
        mobile,
        email,
        address,
        city,
        state,
        gender,
        gstname: gstName,
        gstno: gstNo,
        documenttype: documentType,
        idno: idNo,
        uid: userData?.uid,
        updatedAt: Timestamp.now()
      };

      await updateDoc(doc(db, 'reservations', documentId), {
        customers: customerData,
        bookingsource: bookingSource,
        visitpurpose: visitPurpose,
        adults,
        childs
      });

      if (bookingData.customerId) {
        await updateDoc(doc(db, 'customers', bookingData.customerId), {
          customers: customerData,
          bookingsource: bookingSource,
          visitpurpose: visitPurpose
        });
      }

      setBookingData((prev) => ({
        ...prev,
        customers: customerData,
        bookingsource: bookingSource,
        visitpurpose: visitPurpose,
        adults,
        childs
      }));

      setOpenModal(false);
      Toast.fire({ icon: 'success', title: 'Customer Details Successfully Updated' });
    } catch (error) {
      Toast.fire({ icon: 'error', title: error.message });
    }
  };

  const handleMobileChange = (e) => {
    const val = e.target.value.replace(/\D/g, '');
    if (val.length <= 10) setMobile(val);
  };

  const handlePrint = () => {
    window.print();
  };

  if (!bookingData) {
    return (
      <Box sx={{ p: 6, textAlign: 'center', bgcolor: colors.lightBg, minHeight: '100vh' }}>
        <Typography variant="h6" color={colors.darkGreen}>
          Loading Invoice Details...
        </Typography>
      </Box>
    );
  }

  return (
    <div className="page-fade-in">
      {/* Print-specific style rules */}
      <style>{`
        @page {
          size: A4;
          margin: 5mm;
        }

        @media print {
          html, body, #root {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            background: #fff !important;
            color: #000 !important;
          }

          .no-print, nav, header, footer, .sidebar, button, .MuiTextField-root {
            display: none !important;
          }

          .page-fade-in {
            padding: 0 !important;
            margin: 0 !important;
            max-width: 100% !important;
            width: 100% !important;
          }

          .print-area {
            border: 1px solid #000 !important;
            background: #fff !important;
            margin: 0 auto !important;
            padding: 10px !important;
            width: 100% !important;
            box-shadow: none !important;
            display: block !important;
          }

          .invoice-box-border {
            border: 1px solid #000 !important;
            background: transparent !important;
          }

          table {
            width: 100% !important;
            border-collapse: collapse !important;
          }

          th, td {
            border: 1px solid #000 !important;
            padding: 5px 8px !important;
            color: #000 !important;
          }

          th {
            background-color: #f2f2f2 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            font-weight: bold !important;
          }

          * {
            box-shadow: none !important;
            text-shadow: none !important;
            animation: none !important;
          }
        }
      `}</style>

      <Box
        sx={{
          bgcolor: '#EDF0F2',
          minHeight: '100vh',
          py: { xs: 2, md: 2 },
          px: { xs: 1, md: 1 }
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Grid className="row" spacing={3}>
            {/* INVOICE MAIN CONTAINER */}
            <Grid item xs={12} className="col-sm-12 print-area">
              <Paper
                elevation={3}
                className=""
                sx={{
                  p: { xs: 2, sm: 4 },
                  bgcolor: '#FFFFFF',
                  borderRadius: 2,
                  boxShadow: '0 8px 24px rgba(6, 54, 45, 0.08)'
                }}
              >
                {/* TOP HEADER SECTION */}
                <Box
                  className="invoice-box-border"
                  sx={{
                    borderRadius: 1.5,
                    p: 2,
                    mb: 2.5,
                    bgcolor: '#FAFDF9'
                  }}
                >
                  <Grid className="row" alignItems="center" spacing={2}>
                    <Grid item xs={12} className="col-sm-8" textAlign="left">
                      {propData.logo && (
                        <Box sx={{ mb: 1.5 }}>
                          <img
                            src={propData.logo}
                            alt="Hotel Logo"
                            style={{ maxHeight: '60px', objectFit: 'contain' }}
                          />
                        </Box>
                      )}
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          color: colors.darkGreen,
                          letterSpacing: 0.5
                        }}
                      >
                        {propData.hotelname || 'HOTEL RESIDENCE'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: colors.goldenrod, mt: 0.3 }}
                      >
                        GSTIN: {propData.gstno || propData.gstNo || 'UNREGISTERED'}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: '#4A5568', mt: 0.5 }}>
                        {[propData.address, propData.city, propData.state, propData.pincode]
                          .filter(Boolean)
                          .join(', ')}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: '#4A5568', fontWeight: 500 }}>
                        Tel: +91 {propData.mobile || '-'} | Email: {propData.email || '-'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} className="col-sm-4" textAlign={{ xs: 'left', sm: 'right' }}>
                      <Box
                        sx={{
                          display: 'inline-block',
                          bgcolor: colors.darkGreen,
                          color: colors.gold,
                          px: 2,
                          py: 0.8,
                          borderRadius: 1,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 1 }}>
                          TAX INVOICE
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, mt: 1.5, color: colors.darkGreen }}>
                        Invoice No: <span style={{ color: colors.goldenrod }}>{bookingData.invoiceno || 'N/A'}</span>
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: '#555' }}>
                        Date: {moment().format('DD/MM/YYYY hh:mm A')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* GUEST & INVOICE DETAILS GRID */}
                <Box
                  className="invoice-box-border"
                  sx={{
                    border: `1px solid ${colors.borderGrey}`,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    mb: 3
                  }}
                >
                  <Grid className="row">
                    <Grid className="col-sm-6"
                      item
                      xs={12}
                      sm={6}
                      sx={{
                        borderRight: { sm: `1px solid ${colors.borderGrey}` },
                        borderBottom: { xs: `1px solid ${colors.borderGrey}`, sm: 'none' },
                        p: 1.5
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.8 }}>
                        <PersonIcon sx={{ color: colors.calPolyGreen, fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.darkGreen }}>
                          Billed To (Guest Details)
                        </Typography>
                      </Box>
                      <Table size="small" sx={{ '& td': { border: 0, py: 0.3, px: 0.5, fontSize: '0.82rem' } }}>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ width: '35%', color: '#666', fontWeight: 600 }}>Name:</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: colors.darkGreen }}>
                              {bookingData.customers?.custname || '-'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#666', fontWeight: 600 }}>Phone / Email:</TableCell>
                            <TableCell>
                              {bookingData.customers?.mobile || '-'} / {bookingData.customers?.email || '-'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#666', fontWeight: 600 }}>Address:</TableCell>
                            <TableCell>
                              {[bookingData.customers?.address, bookingData.customers?.city, bookingData.customers?.state]
                                .filter(Boolean)
                                .join(', ') || '-'}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#666', fontWeight: 600 }}>GSTIN / Entity:</TableCell>
                            <TableCell>
                              {bookingData.customers?.gstno
                                ? `${bookingData.customers.gstno} (${bookingData.customers.gstname || ''})`
                                : '-'}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Grid>

                    <Grid className="col-sm-6" item xs={12} sm={6} sx={{ p: 1.5, bgcolor: '#FAFAFA' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 0.8 }}>
                        <HotelIcon sx={{ color: colors.calPolyGreen, fontSize: 20 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.darkGreen }}>
                          Stay & Room Particulars
                        </Typography>
                      </Box>
                      <Table size="small" sx={{ '& td': { border: 0, py: 0.3, px: 0.5, fontSize: '0.82rem' } }}>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ width: '40%', color: '#666', fontWeight: 600 }}>Check-In:</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {moment(bookingData.checkindate).format('DD/MM/YYYY')} {bookingData.checkintime || ''}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#666', fontWeight: 600 }}>Check-Out:</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>
                              {moment(bookingData.checkoutdate).format('DD/MM/YYYY')} {bookingData.checkouttime || ''}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#666', fontWeight: 600 }}>Occupancy:</TableCell>
                            <TableCell>
                              {bookingData.adults || 1} Adults, {bookingData.childs || 0} Children
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#666', fontWeight: 600 }}>Total Days / Nights:</TableCell>
                            <TableCell sx={{ fontWeight: 700, color: colors.goldenrod }}>
                              {bookingData.noofdays || 1} Night(s)
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Grid>
                  </Grid>
                </Box>

                {/* ROOM PARTICULARS TABLE */}
                <TableContainer
                  component={Paper}
                  variant="outlined"
                  sx={{
                    borderRadius: 1.5,
                    borderColor: colors.borderGrey,
                    overflow: 'hidden',
                    boxShadow: 'none'
                  }}
                >
                  <Table size="small">
                    <TableHead sx={{ bgcolor: colors.darkGreen }}>
                      <TableRow sx={{ '& th': { color: colors.gold, fontWeight: 700, py: 1 } }}>
                        <TableCell>Item / Room Description</TableCell>
                        <TableCell align="center">Room No</TableCell>
                        <TableCell>Meal Plan</TableCell>
                        <TableCell align="right">Daily Tariff</TableCell>
                        <TableCell align="center">Nights</TableCell>
                        <TableCell align="right">Amount (₹)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bookingData.rooms?.map((item, index) => {
                        const amount = parseFloat(item.amount || item.roomrate || 0);
                        const days = parseFloat(bookingData.noofdays || 1);
                        const total = amount * days;

                        return (
                          <TableRow
                            key={index}
                            sx={{
                              '&:nth-of-type(even)': { bgcolor: '#FBFBFB' },
                              '& td': { py: 1.2 }
                            }}
                          >
                            <TableCell sx={{ fontWeight: 600, color: colors.darkGreen }}>
                              {item.roomtypename || 'Standard Room'}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={item.roomno || '-'}
                                size="small"
                                sx={{
                                  fontWeight: 700,
                                  bgcolor: '#E8F5E9',
                                  color: colors.calPolyGreen
                                }}
                              />
                            </TableCell>
                            <TableCell>{item.planname || 'EP Plan'}</TableCell>
                            <TableCell align="right">{amount.toFixed(2)}</TableCell>
                            <TableCell align="center">{days}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {total.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* FINANCIAL TOTALS */}
                <Grid className="row" spacing={2} sx={{ mt: 1.5 }} justifyContent="space-between">
                  <Grid className="col-sm-7" item xs={12} sm={6}>
                    <Box
                      className="invoice-box-border"
                      sx={{
                        p: 1.5,
                        border: `1px dashed ${colors.borderGrey}`,
                        borderRadius: 1.5,
                        bgcolor: '#FBFBFB',
                        height: '100%'
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700, color: colors.darkGreen }}>
                        Terms & Conditions:
                      </Typography>
                      <Typography variant="caption" component="div" sx={{ color: '#666', mt: 0.5, lineHeight: 1.4 }}>
                        1. Check-out time is strict as per property policy.<br />
                        2. All disputes are subject to local jurisdiction.<br />
                        3. Goods & Services once delivered are non-refundable.
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid className="col-sm-5" item xs={12} sm={5}>
                    <Box
                      className="invoice-box-border"
                      sx={{ border: `1px solid ${colors.borderGrey}`, borderRadius: 1.5, overflow: 'hidden' }}
                    >
                      <Table size="small" sx={{ '& td': { py: 0.7, px: 1.5 } }}>
                        <TableBody>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, color: '#555' }}>Taxable Subtotal</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>₹{subtotal}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#666' }}>CGST (2.5%)</TableCell>
                            <TableCell align="right">₹{cgst}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#666' }}>SGST (2.5%)</TableCell>
                            <TableCell align="right">₹{sgst}</TableCell>
                          </TableRow>
                          <TableRow sx={{ bgcolor: '#FFFDF0', borderTop: `1px solid ${colors.gold}` }}>
                            <TableCell sx={{ fontWeight: 800, color: colors.darkGreen }}>Grand Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: colors.darkGreen, fontSize: '1rem' }}>
                              ₹{bookingData.totalamount}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ color: '#666' }}>Paid Advance</TableCell>
                            <TableCell align="right" sx={{ color: 'green', fontWeight: 600 }}>
                              ₹{bookingData.advance || 0}
                            </TableCell>
                          </TableRow>
                          <TableRow sx={{ bgcolor: '#FEECEC' }}>
                            <TableCell sx={{ fontWeight: 800, color: '#C62828' }}>Balance Due</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: '#C62828' }}>
                              ₹{bookingData.remaining || 0}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  </Grid>
                </Grid>

                {/* AMOUNT IN WORDS */}
                <Box
                  className="invoice-box-border"
                  sx={{
                    mt: 2,
                    py: 1.2,
                    px: 2,
                    bgcolor: '#FAFDF9',
                    border: `1px solid ${colors.vanilla}`,
                    borderRadius: 1
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: colors.darkGreen }}>
                    <span style={{ color: colors.goldenrod, textTransform: 'uppercase' }}>Amount in Words: </span>
                    <span style={{ fontStyle: 'italic', textTransform: 'capitalize' }}>
                      {amountInWords ? `${amountInWords} Rupees Only` : 'Zero Rupees Only'}
                    </span>
                  </Typography>
                </Box>

                {/* PAYMENT BREAKDOWN */}
                {payHistory.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: colors.darkGreen, mb: 1 }}>
                      Payment Transaction History
                    </Typography>
                    <Table size="small" sx={{ border: `1px solid ${colors.borderGrey}` }}>
                      <TableHead sx={{ bgcolor: '#F0F4F2' }}>
                        <TableRow sx={{ '& th': { fontWeight: 700, color: colors.darkGreen } }}>
                          <TableCell>Timestamp</TableCell>
                          <TableCell>Mode of Payment</TableCell>
                          <TableCell align="right">Amount Paid</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {payHistory.map((pay, i) => (
                          <TableRow key={i}>
                            <TableCell>{pay.createdat}</TableCell>
                            <TableCell>{pay.paymode1 || 'Cash / UPI'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                              ₹{pay.data.amount || bookingData.advance}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {/* SIGNATURE SECTION */}
                <Grid className="row" sx={{ mt: 5, pt: 2 }} alignItems="flex-end">
                  <Grid item className="col-sm-6" textAlign="left">
                    <Box sx={{ width: 180, borderBottom: '1px solid #444', mb: 0.8 }} />
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#555' }}>
                      Guest Signature
                    </Typography>
                  </Grid>

                  <Grid item className="col-sm-6" textAlign="right">
                    {propData.stamp && (
                      <Box sx={{ mb: 1 }}>
                        <img
                          src={propData.stamp}
                          alt="Property Stamp"
                          style={{ maxHeight: '75px', objectFit: 'contain' }}
                        />
                      </Box>
                    )}
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: colors.darkGreen }}>
                      For {propData.hotelname || 'Hotel Administration'}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ color: '#666' }}>
                      Authorized Signatory
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* ACTION BUTTON (Marked no-print) */}
              <Box className="no-print" sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  sx={{
                    bgcolor: colors.darkGreen,
                    color: colors.gold,
                    fontWeight: 700,
                    px: 4,
                    py: 1,
                    '&:hover': { bgcolor: colors.calPolyGreen }
                  }}
                >
                  Print Invoice
                </Button>
              </Box>
            </Grid>

            {/* SIDEBAR PANEL (Marked no-print) */}
            <Grid item xs={12} className="col-sm-3 no-print">
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: '#FFFFFF',
                  border: `1px solid ${colors.borderGrey}`
                }}
              >
                <Box textAlign="center" mb={2}>
                  <ReceiptLongIcon sx={{ fontSize: 48, color: colors.calPolyGreen, mb: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 800, color: colors.darkGreen }}>
                    {bookingData.customers?.custname}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {bookingData.customers?.mobile}
                  </Typography>
                </Box>

                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleOpenEdit}
                  sx={{
                    borderColor: colors.goldenrod,
                    color: colors.darkGreen,
                    fontWeight: 700,
                    mb: 3,
                    '&:hover': {
                      borderColor: colors.darkGreen,
                      bgcolor: 'rgba(21, 83, 50, 0.05)'
                    }
                  }}
                >
                  Edit Guest Data
                </Button>

                <Divider sx={{ mb: 2 }} />

                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.darkGreen, mb: 1 }}>
                  Quick Meta
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ color: '#666' }}>Email:</span>
                    <strong>{bookingData.customers?.email || '-'}</strong>
                  </Box> */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ color: '#666' }}>City:</span>
                    <strong>{bookingData.customers?.city || '-'}</strong>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ color: '#666' }}>Gender:</span>
                    <strong>{bookingData.customers?.gender || '-'}</strong>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                    <span style={{ color: '#666' }}>Source:</span>
                    <strong>{bookingData.bookingsource || '-'}</strong>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* EDIT GUEST DETAILS MODAL (Marked no-print) */}
        <Dialog
          className="no-print"
          open={openModal}
          onClose={() => setOpenModal(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ bgcolor: colors.darkGreen, color: colors.gold, fontWeight: 700 }}>
            Update Customer Information
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid className="row" spacing={2}>
              <Grid item xs={12} className="col-sm-4 mt-3">
                <TextField
                  fullWidth
                  size="small"
                  label="Customer Name"
                  value={custName}
                  onChange={(e) => setCustName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} className="col-sm-4 mt-3">
                <TextField
                  fullWidth
                  size="small"
                  label="Mobile Number"
                  value={mobile}
                  onChange={handleMobileChange}
                />
              </Grid>
              <Grid item xs={12} className="col-sm-4 mt-3">
                <TextField
                  fullWidth
                  size="small"
                  label="Email Address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Grid>

              <Grid  className="col-sm-4 mt-3" item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Street Address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </Grid>
              <Grid  className="col-sm-4 mt-3" item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="City"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                />
              </Grid>
              <Grid  className="col-sm-4 mt-3" item xs={12} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="State"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} className="col-sm-4 mt-3">
                <FormControl fullWidth size="small">
                  <InputLabel>Gender</InputLabel>
                  <Select value={gender} label="Gender" onChange={(e) => setGender(e.target.value)}>
                    <MenuItem value="Male">Male</MenuItem>
                    <MenuItem value="Female">Female</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} className="col-sm-4 mt-3">
                <TextField
                  fullWidth
                  size="small"
                  label="GST Company Name"
                  value={gstName}
                  onChange={(e) => setGstName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} className="col-sm-4 mt-3">
                <TextField
                  fullWidth
                  size="small"
                  label="GSTIN Number"
                  value={gstNo}
                  onChange={(e) => setGstNo(e.target.value)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: '#F8F9FA' }}>
            <Button onClick={() => setOpenModal(false)} sx={{ color: '#555' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={updateCust}
              sx={{
                bgcolor: colors.darkGreen,
                color: colors.gold,
                fontWeight: 700,
                '&:hover': { bgcolor: colors.calPolyGreen }
              }}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
}

export default Hotelinvoice;